import React, { useState, useContext, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './CheckDealTags.module.css';
import AuthContext from '../../context/AuthContext';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import QueueNotificationPopup from '../../components/UI/QueueNotificationPopup';
import StarIcon from '../../assets/dashboard_assets/star.png';
import step1Image from '../../assets/listing_assets/deal_tag_step1.png';
import step2Image from '../../assets/listing_assets/deal_tag_step2.png';
import step3Image from '../../assets/listing_assets/deal_tag_step3.png';
import { triggerLowCreditsModal } from '../../utils/lowCreditsModal';
import useSingleFlight from '../../hooks/useSingleFlight';

// Modals across the app render through the #modal-root portal so they escape the
// page's stacking and overflow contexts. Falls back to rendering in place if the
// portal target is ever missing, rather than silently dropping the dialog.
const renderInModalRoot = (content) => {
    const portalElement = typeof document !== 'undefined'
        ? document.getElementById('modal-root')
        : null;
    return portalElement ? ReactDOM.createPortal(content, portalElement) : content;
};

const CheckDealTags = () => {
    const authCtx = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [asinTags, setAsinTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showOverflowModal, setShowOverflowModal] = useState(false);
    const [overflowAsins, setOverflowAsins] = useState([]);
    const [tokenBalance, setTokenBalance] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [isRequestInProgress, setIsRequestInProgress] = useState(false);
    const [showZipcodeModal, setShowZipcodeModal] = useState(false);
    const [selectedZipcodes, setSelectedZipcodes] = useState([]);
    const actionsRef = useRef(null);

    // Fetch current token balance
    const fetchTokenBalance = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/credits/tokens/balance`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setTokenBalance(data.tokens);
                return data.tokens; 
            } else {
                setTokenBalance(null);
                return null;
            }
        } catch (error) {
            setTokenBalance(null);
            return null;
        }
    };

    // Fetch token balance on component mount
    useEffect(() => {
        if (authCtx.token) {
            fetchTokenBalance();
        }
    }, [authCtx.token]);

    // Check for success messages from token purchase redirect
    useEffect(() => {
        if (location.state?.message && location.state?.type === 'success') {
            // Show success message temporarily
            setError('');
            // You could add a success state here or just refresh token balance
            if (authCtx.token) {
                fetchTokenBalance();
            }

            // Clear the location state to prevent showing message on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, authCtx.token]);

    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addAsinTag();
        }
    };

    const addAsinTag = () => {
        const trimmedInput = inputValue.trim();
        if (!trimmedInput) {
            setError('Please enter a valid ASIN');
            return;
        }

        // Split by commas, line breaks, or whitespace and process multiple ASINs
        const asins = trimmedInput.split(/[\s,\n\r]+/).map(asin => asin.trim().toUpperCase()).filter(asin => asin.length === 10);

        if (asins.length === 0) {
            setError('Please enter at least one valid ASIN (10 characters)');
            return;
        }

        // Check for 100 ASIN limit
        const uniqueAsins = [...new Set([...asinTags, ...asins])];
        
        if (uniqueAsins.length > 100) {
            const acceptedAsins = uniqueAsins.slice(0, 100);
            const rejectedAsins = uniqueAsins.slice(100);

            setAsinTags(acceptedAsins);
            setOverflowAsins(rejectedAsins);
            setShowOverflowModal(true);
            setInputValue('');
            setError('');

            // Scroll to actions after a short delay to allow DOM update
            setTimeout(() => {
                if (actionsRef.current) {
                    actionsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
            return;
        }

        setAsinTags(uniqueAsins);
        setInputValue('');
        setError('');

        // Scroll to actions after a short delay to allow DOM update
        setTimeout(() => {
            if (actionsRef.current) {
                actionsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100);
    };

    const removeAsinTag = (asinToRemove) => {
        setAsinTags(asinTags.filter(asin => asin !== asinToRemove));
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const asins = pastedText.split(/[\s,\n\r]+/).map(asin => asin.trim().toUpperCase()).filter(asin => asin.length === 10);

        const uniqueAsins = [...new Set([...asinTags, ...asins])];

        if (uniqueAsins.length > 100) {
            const acceptedAsins = uniqueAsins.slice(0, 100);
            const rejectedAsins = uniqueAsins.slice(100);

            setAsinTags(acceptedAsins);
            setOverflowAsins(rejectedAsins);
            setShowOverflowModal(true);
            setInputValue('');
            setError('');

            // Scroll to actions after a short delay to allow DOM update
            setTimeout(() => {
                if (actionsRef.current) {
                    actionsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
            return;
        }

        setAsinTags(uniqueAsins);
        setInputValue('');
        setError('');

        // Scroll to actions after a short delay to allow DOM update
        setTimeout(() => {
            if (actionsRef.current) {
                actionsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100);
    };

    // Calculate total tokens needed (ASINs * zipcodes)
    const calculateRequiredTokens = () => {
        const asinCount = asinTags.length;
        if (asinCount === 0) return 0;
        
        // If no zipcodes selected OR only default (560001) selected, use default behavior
        if (selectedZipcodes.length === 0 || 
            (selectedZipcodes.length === 1 && selectedZipcodes[0] === '560001')) {
            return asinCount;
        }
        
        // If multiple zipcodes OR one non-default zipcode, multiply ASINs by zipcodes
        return asinCount * selectedZipcodes.length;
    };

    // Validate token balance before proceeding
    const validateTokenBalance = async () => {
        try {
            const currentBalance = await fetchTokenBalance();
            
            if (currentBalance === null) {
                setError('Unable to verify credit balance. Please try again.');
                return false;
            }
            
            const requiredTokens = calculateRequiredTokens();
            if (currentBalance < requiredTokens) {
                triggerLowCreditsModal();
                setError(`Insufficient credits. Required: ${requiredTokens}, Available: ${currentBalance}`);
                return false;
            }
            
            return true;
        } catch (error) {
            setError('Unable to verify credit balance. Please try again.');
            return false;
        }
    };

    const checkDealTagsInstantly = async () => {
        if (asinTags.length === 0) {
            setError('Please add at least one ASIN');
            return;
        }

        // Prevent multiple simultaneous requests
        if (isRequestInProgress) {
            setError('A request is already in progress. Please wait.');
            return;
        }

        if (!(await validateTokenBalance())) {
            return;
        }

        setIsRequestInProgress(true);
        setLoading(true);
        setError('');

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            
            // Use the unified API endpoint - it handles both single and multiple ASINs
            const requestBody = asinTags.length === 1 
                ? { asin: asinTags[0] } 
                : { asins: asinTags };
            
            // Add zipcodes if multiple selected (more than just Bangalore)
            if (selectedZipcodes.length > 1 || (selectedZipcodes.length === 1 && selectedZipcodes[0] !== '560001')) {
                requestBody.zipcodes = selectedZipcodes;
            }

            const response = await fetch(`${API_URL}/utility_dealtag/check-deal-tags`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();


            if (!response.ok) {
                // Handle specific errors
                if (response.status === 402 || data.error_code === 'NO_CREDITS') {
                    triggerLowCreditsModal();
                    setError(data.message || 'Insufficient credits for this request');
                } else if (response.status === 409) {
                    // Duplicate request error
                    setError(data.message || 'A similar request is already being processed. Please wait for it to complete.');
                } else {
                    throw new Error(data.error || 'Failed to check deal tags');
                }
                return;
            }

            // Check if it's an async response (202 status)
            if (response.status === 202) {
                // Async processing - show success popup
                setTaskId(data.task_id);
                setShowSuccessPopup(true);
                setSelectedZipcodes([]); // Reset selected zipcodes
                setShowZipcodeModal(false); // Close zipcode modal if open

                // Live-update credits the moment the task is queued (backend already
                // debited atomically). Avoids users needing to refresh to see the
                // new balance in the navbar / on this page.
                if (data.credits && typeof data.credits.remaining === 'number') {
                    if (authCtx.updateUserCredit) {
                        authCtx.updateUserCredit(data.credits.remaining);
                    }
                    if (authCtx.updateUserTokens) {
                        authCtx.updateUserTokens(data.credits.remaining);
                    }
                    setTokenBalance(data.credits.remaining);
                }

                // Start polling for results
                pollTaskStatus(data.task_id);
            } else {
                // Sync processing - reset form after successful processing
                // Refresh token balance after successful operation
                await fetchTokenBalance();

                // Update AuthContext tokens if available
                if (authCtx.updateUserTokens && tokenBalance !== null) {
                    const tokensConsumed = data.tokens_consumed || asinTags.length;
                    const newBalance = Math.max(0, tokenBalance - tokensConsumed);
                    authCtx.updateUserTokens(newBalance);
                }
                
                // Reset zipcodes and close modal
                setSelectedZipcodes([]);
                setShowZipcodeModal(false);
                
                // Reset form after successful processing
                resetFormFields();
            }

        } catch (err) {
            setError(err.message || 'Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
            setIsRequestInProgress(false);
        }
    };

    // isRequestInProgress is only set after the awaited balance check, leaving the
    // button live for that whole round trip — this closes that window.
    const handleCheckInstantly = useSingleFlight(checkDealTagsInstantly);

    // Poll task status for async results
    const pollTaskStatus = async (taskId) => {
        const maxAttempts = 60; // Poll for up to 5 minutes (5 seconds * 60)
        let attempts = 0;

        const poll = async () => {
            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const response = await fetch(`${API_URL}/utility_dealtag/task-status/${taskId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authCtx.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();

                if (data.status === 'SUCCESS') {
                    // Refresh token balance after successful operation
                    await fetchTokenBalance();

                    // Update AuthContext tokens if available
                    if (authCtx.updateUserTokens && tokenBalance !== null) {
                        const tokensConsumed = data.result.tokens_consumed || asinTags.length;
                        const newBalance = Math.max(0, tokenBalance - tokensConsumed);
                        authCtx.updateUserTokens(newBalance);
                    }
                    
                    setShowSuccessPopup(false);
                    setTaskId(null);
                    return;
                } else if (data.status === 'FAILURE') {
                    setError(data.error || 'Task failed to complete');
                    setShowSuccessPopup(false);
                    return;
                } else if (data.status === 'PROGRESS') {
                    // Continue polling
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, 5000); // Poll every 5 seconds
                    } else {
                        setError('Task is taking longer than expected. Please check back later.');
                        setShowSuccessPopup(false);
                    }
                }
            } catch (err) {
                setError('Failed to check task status');
                setShowSuccessPopup(false);
            }
        };

        // Start polling after a short delay
        setTimeout(poll, 2000);
    };

    // History lives on the Manage page now — open it with the Deal Tag filter preselected
    const handleViewHistory = () => {
        navigate('/dashboard/projects', { state: { projectTypeFilter: 'deal_tag' } });
    };

    // Reset input fields without forcing a view change
    const resetFormFields = () => {
        setInputValue('');
        setAsinTags([]);
        setError('');
        setShowSuccessPopup(false);
        setTaskId(null);
    };

    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
    };



    return (
        <div className={styles.container}>

            <div className={styles.newSearchPage}>
                    {/* Title */}
                    <div className={styles.newSearchTitleContainer}>
                        <div className={styles.newSearchTitle}>Check Deal Tags for Amazon</div>
                        <div className={styles.newSearchTitleLine}></div>
                    </div>

                    {/* Search Form */}
                    <div className={styles.newSearchFormContainer}>
                        <form onSubmit={(e) => { e.preventDefault(); addAsinTag(); }} className={styles.newSearchForm}>
                            {/* Input Field */}
                            <div className={styles.newSearchInputContainer}>
                                <div className={styles.newSearchInputCard}>
                                    <div className={styles.newSearchInputWrapper}>
                                        {/* ASIN Chips */}
                                        {asinTags.map((asin) => (
                                            <div key={asin} className={styles.asinChip}>
                                                <span className={styles.asinChipText}>{asin}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAsinTag(asin)}
                                                    className={styles.asinChipRemove}
                                                    disabled={loading}
                                                    aria-label={`Remove ${asin}`}
                                                >
                                                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8 0.7L7.3 0L4 3.3L0.7 0L0 0.7L3.3 4L0 7.3L0.7 8L4 4.7L7.3 8L8 7.3L4.7 4L8 0.7Z" fill="#505050"/>
</svg>
                                                </button>
                                            </div>
                                        ))}
                                        <input
                                            type="text"
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onKeyPress={handleInputKeyPress}
                                            onPaste={handlePaste}
                                            placeholder={asinTags.length === 0 ? "Enter ASIN(s) and press Enter — you can paste a single ASIN, multiple ASINs, or an entire column" : ""}
                                            className={styles.newSearchInput}
                                            disabled={loading}
                                            maxLength={10}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.dealTagActionsContainer}>
                                <button
                                    onClick={() => setShowZipcodeModal(true)}
                                    className={styles.historyButton}
                                    disabled={loading || isRequestInProgress}
                                    type="button"
                                >
                                    <span>Zipcode</span>
                                </button>
                                <button
                                    onClick={handleCheckInstantly}
                                    className={styles.newAnalyseButton}
                                    disabled={loading || isRequestInProgress || asinTags.length === 0}
                                    type="button"
                                >
                                    {loading ? (
                                        <>
                                            <span className={styles.analyseButtonText}>Checking...</span>
                                        </>
                                    ) : (
                                        <>
                                            <img src={StarIcon} alt="Credits" className={styles.starIcon} />
                                            {asinTags.length > 0 && (
                                                <span className={styles.asinCountBadge}>{calculateRequiredTokens()}</span>
                                            )}
                                            <span className={styles.analyseButtonText}>Check Instantly</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleViewHistory}
                                    className={styles.historyButton}
                                    disabled={loading || isRequestInProgress}
                                    type="button"
                                >
                                    <span>History</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            <div className={styles.errorContent}>
                                <p>{error}</p>
                                {(error.includes('Insufficient credits') || error.includes('Unable to verify credit balance')) && (
                                    <button
                                        className={styles.buyTokensButton}
                                        onClick={() => navigate('/dashboard/plans')}
                                    >
                                        Buy Credits
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* How it works Section */}
                    <div className={styles.newHowItWorksSection}>
                        <div className={styles.newHowItWorksHeader}>
                            <div className={styles.howItWorksBadge}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.7">
                                        <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_361_1889)"/>
                                        <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_361_1889)"/>
                                    </g>
                                    <defs>
                                        <linearGradient id="paint0_linear_361_1889" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                        <linearGradient id="paint1_linear_361_1889" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span>How it works</span>
                            </div>
                        </div>

                        <div className={styles.newHowItWorksSteps}>
                            {/* Step 1 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step1Image} 
                                        alt="Step 1: Pickup the ASIN from the Product URL" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>1. Pickup the ASIN from the Product URL</div>
                            </div>

                            {/* Step 2 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step2Image} 
                                        alt="Step 2: Paste it on the box above" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>2. Paste it on the box above</div>
                            </div>

                            {/* Step 3 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step3Image} 
                                        alt="Step 3: Click on Analyse...and done" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>3. Click on Analyse...and done</div>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Overflow Modal */}
            {showOverflowModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3 className={styles.modalTitle}>Too Many ASINs</h3>
                        <p className={styles.modalDescription}>
                            You've pasted <strong>{asinTags.length + overflowAsins.length} ASINs</strong>, but we can only process <strong>100 ASINs</strong> at a time.
                        </p>
                        <p className={styles.modalDescription}>
                            We've added the <strong>first 100 ASINs</strong> to your list. The remaining <strong>{overflowAsins.length} ASINs</strong> were not added.
                        </p>
                        <div className={styles.modalSuggestions}>
                            <p className={styles.suggestionTitle}>What you can do:</p>
                            <ul className={styles.suggestionList}>
                                <li>Process the current 100 ASINs first, then add the remaining ones</li>
                                <li>Remove some ASINs from the current list to make room for others</li>
                                <li>Split your ASINs into smaller batches of 100 or fewer</li>
                            </ul>
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => {
                                    setShowOverflowModal(false);
                                    setOverflowAsins([]);
                                }}
                                className={styles.confirmButton}
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup for Async Processing */}
            <QueueNotificationPopup
                show={showSuccessPopup}
                onClose={handleCloseSuccessPopup}
                onResetFiles={resetFormFields}
                title="Deal Check Started"
            />

            {/* Zipcode Modal */}
            {showZipcodeModal && renderInModalRoot(
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3 className={styles.modalTitle}>Select Zipcode</h3>
                        <div className={styles.modalDescription}>
                            Select one or more zipcodes to check deal tags for different regions.
                        </div>
                        <div className={styles.zipcodeList}>
                            {[
                                { region: 'North', city: 'Delhi', zipcode: '110001' },
                                { region: 'South', city: 'Bangalore', zipcode: '560001' },
                                { region: 'East', city: 'Kolkata', zipcode: '700001' },
                                { region: 'West', city: 'Mumbai', zipcode: '400001' },
                                { region: 'South', city: 'Hyderabad', zipcode: '500001' },
                                { region: 'South', city: 'Chennai', zipcode: '600001' }
                            ].map((item) => (
                                <label key={item.zipcode} className={styles.zipcodeItem}>
                                    <input
                                        type="checkbox"
                                        checked={selectedZipcodes.includes(item.zipcode)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedZipcodes([...selectedZipcodes, item.zipcode]);
                                            } else {
                                                setSelectedZipcodes(selectedZipcodes.filter(z => z !== item.zipcode));
                                            }
                                        }}
                                        className={styles.zipcodeCheckbox}
                                    />
                                    <span className={styles.zipcodeLabel}>
                                        {item.region} - {item.city} - {item.zipcode}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowZipcodeModal(false)}
                                className={styles.confirmButton}
                            >
                                Select
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <LoadingOverlay />}
        </div>
    );
};

export default CheckDealTags;
